import React, { useState } from "react";
import axios from "axios";
import { useHistory } from "react-router";
import { FaTrashAlt } from "react-icons/fa";
import { useSnackbar } from "notistack";
import {
  postAddProductToCartService,
  postRemoveProductFromCart,
  updateProductQtyInCart,
} from "../../api/apiServices";

const CartItemCard = ({ product, isSearch, setSearch, updateCart, onRefetchCart }) => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [isUpdating, setIsUpdating] = useState(false);
  const isSoldOut = (product.product_details?.qty_available ?? 0) <= 0;
  const maxAllowed = product.product_details?.max_quantity ?? Infinity;
  const inStock = product.product_details?.qty_available ?? 0;
  const effectiveMax = Math.min(maxAllowed, inStock);

  const updateHandler = async (type) => {
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      let updatedQuantity;

      if (type === "increment") {
        updatedQuantity = product.quantity + 1;
      } else {
        updatedQuantity = product.quantity - 1;
      }

      const data = {
        product_id: parseInt(product.product_id),
        qty: updatedQuantity,
      };

      const response = await postAddProductToCartService(data);

      if (response?.data?.success === true) {
        // Always re-fetch from server to guarantee UI is in sync
        if (onRefetchCart) {
          await onRefetchCart();
        } else if (response.data.data?.cart) {
          updateCart(response.data.data.cart);
        }
      } else {
        enqueueSnackbar(
          "Error occurred: " + (response?.data?.message || "Unknown error"),
          { variant: "error" }
        );
      }
    } catch (error) {
      enqueueSnackbar("Error occurred: " + error, { variant: "error" });
      console.error("Error:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const removeProduct = async (e) => {
    if (e) e.stopPropagation();
    if (isUpdating) return;
    setIsUpdating(true);
    try {
      const data = {
        product_id: parseInt(product.product_id),
        qty: 0,
      };

      const response = await postRemoveProductFromCart(data);

      if (response?.status === 200 && response?.data?.success !== false) {
        enqueueSnackbar("Item removed", { variant: "success" });
        // Always re-fetch from server to guarantee UI is in sync
        if (onRefetchCart) {
          await onRefetchCart();
          // Notify Navbar (and any other listeners) to refresh cart count
          window.dispatchEvent(new CustomEvent("cartUpdated"));
        } else {
          const updatedCart = response?.data?.data?.cart ?? response?.data?.cart;
          if (updatedCart) updateCart(updatedCart);
        }
      } else {
        const msg = response?.data?.message || "Could not remove item. Try again.";
        enqueueSnackbar(msg, { variant: "error" });
      }
    } catch (error) {
      console.error("Error:", error);
      enqueueSnackbar(error?.response?.data?.message || "Could not remove item. Try again.", { variant: "error" });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div
      className={`m-auto flex flex-col gap-2 p-4 rounded-sm shadow-sm bg-white/[0.6] mb-2 max-w-xl relative ${
        isSearch ? "cursor-pointer hover:bg-black/5" : ""
      } ${isUpdating ? "opacity-60 pointer-events-none" : ""}`}
      onClick={() => {
        if (isSearch) {
          setSearch("");
          history.push(`products/${product.product_id}`);
        }
      }}
    >
      {!isSearch && (
        <button
          type="button"
          className="absolute top-2 right-2 text-red-600 hover:text-red-700 p-1.5 z-9 bg-white/90 rounded hover:bg-white shadow-sm disabled:opacity-40 disabled:cursor-not-allowed"
          onClick={removeProduct}
          disabled={isUpdating}
          title="Remove from cart"
        >
          <FaTrashAlt className="text-lg" />
        </button>
      )}
      <div className="flex  items-center flex-wrap gap-2 w-full">
        <div className="flex flex-wrap xs:flex-nowrap justify-center xs:justify-start flex-1 items-center gap-5">
          <div
            className={` bg-black/[0.075] ${
              isSearch ? "h-14 w-14 " : "h-28 w-28"
            } rounded-md flex items-center cursor-pointer hover:bg-black/5`}
          >
            <img
              src={`https://pancham-bucket.s3.amazonaws.com/${
                JSON.parse(product.product_details.product_images)[0]
              }`}
              alt=""
              className="object-fit w-full"
              onClick={() => {
                history.push(`products/${product.product_id}`);
              }}
            />
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl py-3 font-semibold">
                {product?.product_details.title}
              </h2>
              {isSoldOut && (
                <span className="bg-red-500 text-white text-xs font-medium px-2 py-1 rounded">
                  Sold out
                </span>
              )}
            </div>
            {isSoldOut && (
              <p className="text-sm text-red-600 -mt-2">No longer available. Remove to proceed to checkout.</p>
            )}
            {!isSearch && (
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-2">
                  <div className="flex gap-2 items-center">
                    <span className="text-gray-700 text-sm">Quantity</span>
                    <div className="flex items-center rounded-md border border-gray-300 overflow-hidden bg-gray-50">
                      <button
                        type="button"
                        className="flex items-center justify-center min-h-[44px] min-w-[44px] bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                        onClick={(e) => { e.stopPropagation(); updateHandler("decrement"); }}
                        disabled={isUpdating}
                      >
                        −
                      </button>
                      <span className="min-w-[3rem] min-h-[44px] flex items-center justify-center bg-white border-x border-gray-300 text-base font-medium">
                        {isUpdating ? "…" : product.quantity}
                      </span>
                      <button
                        type="button"
                        className="flex items-center justify-center min-h-[44px] min-w-[44px] bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (product.quantity >= effectiveMax) {
                            if (inStock > 0 && inStock < maxAllowed) {
                              enqueueSnackbar(`Only ${inStock} remaining`, { variant: "info" });
                            } else {
                              enqueueSnackbar(`Max quantity is ${effectiveMax}`, { variant: "info" });
                            }
                            return;
                          }
                          updateHandler("increment");
                        }}
                        disabled={isSoldOut || isUpdating}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <span className="text-gray-500 text-sm">
                    Max Quantity: {Number.isFinite(effectiveMax) ? effectiveMax : (product.product_details?.max_quantity ?? product.product_details?.qty_available ?? "—")}
                    {inStock > 0 && inStock < maxAllowed && (
                      <span className="text-amber-700 ml-1">(Only {inStock} in stock)</span>
                    )}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end">
          <span>₹{product.quantity > 0 ? Math.round(product.total_price / product.quantity) : product.product_details.selling_price}</span>
          {product.product_details.original_selling_price &&
            product.product_details.original_selling_price > Math.round(product.total_price / product.quantity) && (
            <span className="text-xs line-through text-gray-600">
              ₹ {product.product_details.original_selling_price}
            </span>
          )}
          {!product.product_details.original_selling_price && product.product_details.mrp >
            product.product_details.selling_price && (
            <span className="text-xs line-through text-gray-600">
              ₹ {product.product_details.mrp}
            </span>
          )}
          {/* Offer badge: shown when an active offer is applied to this product */}
          {product.product_details.active_offer && (
            <span
              style={{
                display: "inline-block",
                marginTop: "4px",
                background: "linear-gradient(90deg, #28a745 0%, #20c997 100%)",
                color: "#fff",
                fontSize: "10px",
                fontWeight: "700",
                padding: "2px 7px",
                borderRadius: "12px",
                whiteSpace: "nowrap",
                letterSpacing: "0.2px",
              }}
            >
              After {product.product_details.active_offer.discount_percentage}% discount in{" "}
              {product.product_details.active_offer.sale_name}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CartItemCard;
